import React from "react";
import { motion } from "framer-motion";
import { Crown, Award, Heart, Sparkles } from "lucide-react";
import { useSite } from "../../components/public/PublicLayout";
import { Section, Eyebrow, SectionTitle, FadeIn } from "../../components/public/Section";

export default function About() {
  const { site } = useSite();
  return (
    <>
      <PageHero title="The House of Balaji" subtitle="Eighteen years of plating tradition with reverence." image="https://images.unsplash.com/photo-1712314947761-a8d718bd8c32?auto=format&fit=crop&w=2000&q=80" />

      <Section>
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <FadeIn>
            <img src="https://images.unsplash.com/photo-1681270543584-8e541a1bb056?auto=format&fit=crop&w=1400&q=80" className="w-full h-[560px] object-cover border border-gold/30" alt="Akash Sharma" />
          </FadeIn>
          <FadeIn delay={0.1}>
            <Eyebrow>Founder · Master Caterer</Eyebrow>
            <h2 className="serif text-5xl leading-tight">{site?.founder_name || "Akash Sharma"}</h2>
            <p className="text-navy-900/50 mt-5 leading-relaxed">
              Akashji began Shree Balaji Caterers in 2007 with a single brass pot, two cooks and a stubborn refusal to compromise on flavour. Today, Balaji is Vadodara's most trusted catering atelier — sought after for everything from royal weddings to industrial canteens.
            </p>
            <p className="text-navy-900/50 mt-4 leading-relaxed">
              His philosophy is simple: <em className="text-gold not-italic">"A guest's plate is sacred. Plate it as you would for your own family."</em>
            </p>
            <div className="grid grid-cols-2 gap-4 mt-9">
              {[{ icon: Crown, t: "18+ Years" }, { icon: Award, t: "FSSAI Certified" }, { icon: Heart, t: "2400+ Events" }, { icon: Sparkles, t: "15+ Cuisines" }].map((b, i) => (
                <div key={i} className="border border-gold/30 p-5">
                  <b.icon size={20} className="text-gold" />
                  <div className="mt-2 serif text-lg">{b.t}</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </Section>

      <Section dark>
        <SectionTitle dark title="Our" italic="philosophy." center subtitle="Three pillars that have guided every event we have crafted." />
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: "Reverence", d: "Every guest is a deity. Every plate is an offering. We cook with the calm of temple kitchens." },
            { t: "Craft", d: "We blend Gujarati grandmothers' recipes with hotel-school technique and modern plating." },
            { t: "Service", d: "Quiet, anticipatory, never intrusive. Our waitstaff trains for months before stepping onto your floor." },
          ].map((p, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div className="border border-gold/20 p-8 h-full bg-navy-900/40">
                <div className="serif text-gold text-6xl">{String(i + 1).padStart(2, "0")}</div>
                <div className="serif text-2xl mt-4 text-ivory">{p.t}</div>
                <p className="text-ivory/70 mt-3 leading-relaxed">{p.d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>
    </>
  );
}

export function PageHero({ title, subtitle, image }) {
  return (
    <section className="relative h-[60vh] flex items-end" data-testid="page-hero">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${image})` }} />
      <div className="absolute inset-0 hero-overlay" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 pb-16 w-full">
        <div className="label-eyebrow text-gold">Shree Balaji Caterers</div>
        <h1 className="serif text-ivory text-5xl md:text-7xl leading-tight mt-3">{title}</h1>
        {subtitle && <p className="text-ivory/75 mt-4 max-w-2xl">{subtitle}</p>}
      </motion.div>
    </section>
  );
}
